import { z } from "zod";
import i18n from "@/i18n";

const t = (key: string) => i18n.t(key, { ns: "validation" });

export const stayPackageConfigSchema = z.object({
  appliesToModule: z.literal("daycare"),
  packageKind: z.enum(["session_block", "recurring_plan"]),
  period: z.enum(["weekly", "monthly"]).nullable(),
  daysPerWeek: z.number().int().min(1).max(7).nullable(),
  includesOvernight: z.literal(false),
  sizeRestriction: z.enum(["small", "medium", "large"]).nullable(),
}).superRefine((value, context) => {
  if (value.packageKind === "recurring_plan" && (!value.period || value.daysPerWeek == null)) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["period"],
      message: t("stayPackageRecurringFieldsRequired"),
    });
  }

  if (value.packageKind === "session_block" && (value.period !== null || value.daysPerWeek !== null)) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["packageKind"],
      message: t("stayPackageSessionBlockFieldsMustBeEmpty"),
    });
  }
});

export type StayPackageConfigValue = z.infer<typeof stayPackageConfigSchema>;

export function toStayPackageConfigValue(
  packageKind: StayPackageConfigValue["packageKind"],
  period: StayPackageConfigValue["period"],
  daysPerWeek: StayPackageConfigValue["daysPerWeek"],
): StayPackageConfigValue {
  return {
    appliesToModule: "daycare",
    packageKind,
    period: packageKind === "recurring_plan" ? period : null,
    daysPerWeek: packageKind === "recurring_plan" ? daysPerWeek : null,
    includesOvernight: false,
    sizeRestriction: null,
  };
}

export interface DaycarePackageBaseService {
  id: string;
  name: string;
  sale_price: number | null;
}

export function buildDaycarePackageTemplateItem(service: DaycarePackageBaseService) {
  return {
    itemId: service.id,
    itemNameSnapshot: service.name,
    unitPriceSnapshot: service.sale_price ?? 0,
    sessionsTotal: 1,
    isFreeLine: false,
    sortOrder: 0,
  };
}

export const daycarePackageFormSchema = z.object({
  packageKind: z.enum(["session_block", "recurring_plan"]),
  baseServiceId: z.string().min(1).nullable(),
  sessions: z.number().int().min(1),
  period: z.enum(["weekly", "monthly"]).nullable(),
  daysPerWeek: z.number().int().min(1).max(7).nullable(),
}).superRefine((value, context) => {
  if (!value.baseServiceId) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["baseServiceId"],
      message: t("stayPackageBaseServiceRequired"),
    });
  }

  if (value.packageKind === "recurring_plan" && (!value.period || value.daysPerWeek == null)) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["period"],
      message: t("stayPackageRecurringFieldsRequired"),
    });
  }

  if (value.packageKind === "session_block" && (value.period !== null || value.daysPerWeek !== null)) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["packageKind"],
      message: t("stayPackageSessionBlockFieldsMustBeEmpty"),
    });
  }
});

export type DaycarePackageFormValue = z.infer<typeof daycarePackageFormSchema>;
